import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { VerdictBadge } from "@/components/Badges";
import { toast } from "sonner";
import { UploadCloud, Download, Loader2, FileSpreadsheet } from "lucide-react";

export default function BatchScreening() {
  const { user } = useAuth();
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [batch, setBatch] = useState(null);
  const pollRef = useRef(null);

  const poll = (id) => {
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/batch/${id}`);
        setBatch(data);
        if (data.status === "DONE") {
          clearInterval(pollRef.current);
          toast.success("Batch complete");
        }
      } catch (e) {}
    }, 1500);
  };

  useEffect(() => () => pollRef.current && clearInterval(pollRef.current), []);

  const upload = async () => {
    if (!file) {
      toast.error("Choose a CSV file first");
      return;
    }
    setUploading(true);
    setBatch(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/batch", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Screening ${data.total} entities…`);
      setBatch({ ...data, processed: 0, results: [] });
      poll(data.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const resp = await api.get("/batch/template.csv", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "batch-template.csv";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Download failed");
    }
  };

  const pct = batch && batch.total ? Math.round((batch.processed / batch.total) * 100) : 0;

  return (
    <div className="max-w-4xl space-y-6" data-testid="batch-screening-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-100 sm:text-3xl">
            Batch Screening
          </h1>
          <p className="mt-1 text-sm text-slate-400">
            Upload a CSV to screen up to 200 entities in one run.
          </p>
        </div>
        <button
          data-testid="download-template-btn"
          onClick={downloadTemplate}
          className="flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-300 transition-colors hover:border-slate-600 hover:text-white"
        >
          <Download className="h-4 w-4" /> CSV Template
        </button>
      </div>

      <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-6">
        <div
          onClick={() => fileRef.current?.click()}
          className="flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-700 py-12 text-center transition-colors hover:border-blue-600"
          data-testid="batch-dropzone"
        >
          <FileSpreadsheet className="mb-3 h-10 w-10 text-slate-600" />
          <p className="text-sm text-slate-300">
            {file ? <span className="font-medium text-blue-300">{file.name}</span> : "Click to choose a CSV file"}
          </p>
          <p className="mt-1 font-mono text-[10px] uppercase tracking-wider text-slate-500">
            columns: name, entity_type, jurisdiction, wallet_addresses, activity_notes, flags…
          </p>
          <input
            ref={fileRef}
            data-testid="batch-file-input"
            type="file"
            accept=".csv"
            className="hidden"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
          />
        </div>
        <button
          data-testid="batch-upload-btn"
          onClick={upload}
          disabled={uploading || !file}
          className="mt-4 flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-blue-500 disabled:opacity-60"
        >
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
          Run Batch Screening
        </button>
      </div>

      {batch && (
        <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-6" data-testid="batch-progress">
          <div className="mb-2 flex items-center justify-between text-sm">
            <span className="font-medium text-slate-200">
              {batch.status === "DONE" ? "Completed" : "Processing…"}
            </span>
            <span className="font-mono tabular-nums text-slate-400">
              {batch.processed} / {batch.total}
            </span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-slate-800">
            <div className="h-full bg-blue-500 transition-all" style={{ width: `${pct}%` }} />
          </div>

          {batch.results?.length > 0 && (
            <div className="mt-5 overflow-hidden rounded-lg border border-slate-800">
              <table className="w-full text-sm">
                <thead className="bg-slate-950/60 font-mono text-[10px] uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="px-3 py-2 text-left">#</th>
                    <th className="px-3 py-2 text-left">Entity</th>
                    <th className="px-3 py-2 text-center">Score</th>
                    <th className="px-3 py-2 text-left">Verdict</th>
                    <th className="px-3 py-2 text-left">Case</th>
                  </tr>
                </thead>
                <tbody data-testid="batch-results-table">
                  {batch.results.map((r) => (
                    <tr key={r.row} className="border-t border-slate-800">
                      <td className="px-3 py-2 font-mono text-slate-500">{r.row}</td>
                      <td className="px-3 py-2 text-slate-200">{r.name}</td>
                      <td className="px-3 py-2 text-center font-mono font-bold tabular-nums text-slate-300">
                        {r.risk_score ?? "—"}
                      </td>
                      <td className="px-3 py-2">
                        {r.verdict ? <VerdictBadge verdict={r.verdict} /> : <span className="text-xs text-red-400">{r.error}</span>}
                      </td>
                      <td className="px-3 py-2">
                        {r.screening_id ? (
                          <Link to={`/cases/${r.screening_id}`} className="text-xs text-blue-400 hover:text-blue-300">
                            View →
                          </Link>
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
